import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import type { AppDispatch } from "../../redux/store";
import { setUser } from "../../redux/userSlice";
import helpers from "../../_helpers/common";
import useAxios from "../../hooks/useAxios";
import { CommonInput, SubmitButton } from "../common";

interface LoginFormState {
  email: string;
  password: string;
}

interface ApiValidationErrorResponse {
  type?: string;
  message?: {
    errors?: Partial<LoginFormState>;
  };
}

interface FormData {
  login_type: number;
  email: string;
  password: string;
}

function Login() {
  const axiosInstance = useAxios();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [formData, setFormData] = useState<LoginFormState>({
    email: "",
    password: "",
  });

  // If you don't actually switch login type anywhere, keep it constant (no state needed)
  const loginType = 1;

  const [errors, setErrors] = useState<Partial<LoginFormState>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  /*useEffect(() => {
	  const token = localStorage.getItem("token");
	  if (token) {
		navigate("/dashboard", { replace: true });
	  }
  }, [navigate]);*/

  /* Handle Input Change */
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;

    setErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* Validate Form */
  const validate = (): boolean => {
    // if login type isn't 1, skip validation
    if (loginType !== 1) return true;

    const newErrors: Partial<LoginFormState> = {};

    if (!formData.email?.trim()) {
      newErrors.email = "Email is required";
    } else if (!helpers.isValidEmail(formData.email)) {
      newErrors.email = "Invalid email address";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 6) {
      newErrors.password = "Your password length is too short";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const prepareRequestData = (): FormData => {
    return {
      login_type: loginType,
      email: formData.email,
      password: formData.password,
    };
  };

  /* Submit Form */
  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);

    const form_data = prepareRequestData();

    try {
      const response: any = await axiosInstance({
        url: "users/admin-login",
        method: "POST",
        data: form_data,
      });

      if (!response?.error) {
        const token = response?.data?.authToken;
        const details = response?.data?.user;

        if (token) localStorage.setItem("token", token);

        if (details) {
          localStorage.setItem("user", JSON.stringify(details));
          dispatch(setUser(details));
        }

        navigate("/dashboard", { replace: true });
      }
    } catch (error: any) {
      const apiError: ApiValidationErrorResponse | undefined = error?.response?.data;

      if (apiError?.type === "validation_error") {
        const apiErrors = apiError?.message?.errors;
        if (apiErrors) setErrors(apiErrors);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
  <div className="d-flex flex-column flex-root min-vh-100 overflow-hidden">
    <div className="d-flex flex-column flex-lg-row flex-column-fluid min-vh-100">
      
      {/* LEFT SIDE */}
      <div className="d-flex flex-center flex-lg-row-fluid w-lg-50 p-10 min-vh-100">
        <div className="d-flex flex-center flex-lg-start flex-column text-center text-lg-start">
          <a
            href="https://astroushaa.com"
            target="_blank"
            rel="noreferrer"
            className="mb-7"
          >
            {/* IMPORTANT: responsive logo */}
            <img
              alt="Logo"
              src="/mascot-logo.png"
              className="h-60px h-lg-75px"
              style={{ maxWidth: "100%" }}
            />
          </a>

          <h2 className="text-white fw-normal m-0">
            Access customer kundli requests, ePuja bookings, predictions, remedies, and product sales management in one secure dashboard.
          </h2>
        </div>
      </div>

      {/* RIGHT SIDE */}
      <div className="d-flex flex-center flex-lg-row-auto w-lg-50 p-10 min-vh-100">
        <div className="w-100 w-lg-500px bg-body rounded-4 p-10 shadow-sm">
          <form className="form w-100">
            <div className="text-center mb-11">
              <h1 className="text-gray-900 fw-bolder mb-3">Sign In</h1>
              <div className="text-gray-500 fw-semibold fs-6">
                Welcome To Astrousaa Admin
              </div>
            </div>

            <CommonInput
              type="email"
              name="email"
              placeholder="Enter your email"
              value={formData.email}
              onChange={handleInputChange}
              error={errors.email}
              autoComplete="email"
            />

            <CommonInput
              type="password"
              name="password"
              placeholder="Enter your password"
              value={formData.password}
              onChange={handleInputChange}
              error={errors.password}
              autoComplete="current-password"
            />

            <div className="d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8">
              <div />
              <Link to="/forgot-password" className="link-primary">
                Forgot Password ?
              </Link>
            </div>

            {/* Best: SubmitButton should be <button type="submit"> */}
            <SubmitButton
              isSubmitting={isSubmitting}
              onClick={() => handleSubmit()}
              label="Sign In"
            />
          </form>
        </div>
      </div>
    </div>
  </div>
);

}

export default Login;
